import type * as React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowLeft as ArrowLeftIcon } from "@phosphor-icons/react/ArrowLeft";
import { X as XIcon } from "@phosphor-icons/react/X";

export interface ToolPanelProps {
	title: string;
	subtitle?: string;
	children?: React.ReactNode;
	actions?: React.ReactNode;
	headerActions?: React.ReactNode;
	actionsAlignment?:
		| "flex-start"
		| "center"
		| "flex-end"
		| "space-between"
		| "space-around"
		| "space-evenly";
	onClose?: () => void;
	onBack?: () => void;
}

export function ToolPanel({
	title,
	subtitle,
	children,
	actions,
	headerActions,
	actionsAlignment = "flex-end",
	onClose,
	onBack,
}: ToolPanelProps): React.JSX.Element {
	return (
		<Box
			sx={{
				width: "400px",
				height: "100vh",
				display: "flex",
				flexDirection: "column",
				bgcolor: "var(--mui-palette-background-paper)",
				border: "1px solid var(--mui-palette-divider)",
				boxShadow: "var(--mui-shadows-16)",
			}}>
			{/* Header */}
			<Stack
				direction="row"
				spacing={2}
				sx={{
					alignItems: "center",
					justifyContent: "space-between",
					px: 3,
					py: 2,
				}}>
				<Stack
					direction="row"
					spacing={1}
					sx={{ alignItems: "center", flex: 1 }}>
					{onBack && (
						<IconButton
							onClick={onBack}
							size="small">
							<ArrowLeftIcon />
						</IconButton>
					)}
					<Box>
						<Typography variant="h6">{title}</Typography>
						{subtitle && (
							<Typography
								variant="body2"
								color="text.secondary">
								{subtitle}
							</Typography>
						)}
					</Box>
				</Stack>
				{headerActions && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{headerActions}
					</Box>
				)}
				<IconButton
					onClick={onClose}
					size="small">
					<XIcon />
				</IconButton>
			</Stack>

			{/* Divider */}
			<Divider />

			{/* Main content area */}
			<Box
				sx={{
					flex: 1,
					overflowY: "auto",
					p: 3,
				}}>
				{children}
			</Box>

			{/* Action area */}
			{actions && (
				<>
					<Divider />
					<Box sx={{ p: 3, display: "flex", justifyContent: actionsAlignment }}>
						{actions}
					</Box>
				</>
			)}
		</Box>
	);
}
